import { useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableItemRow from '../../components/admin/SortableItemRow.jsx';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '../../api/client.js';
import {
  PageHeader, SearchBox, SortHeader, useSort, useTextFilter,
  EmptyState, ConfirmDialog, Modal, FormSection, Badge
} from '../../components/admin/AdminUI.jsx';

const BLANK_ITEM = {
  itemType: 'Report', surface: 'Desktop', groupLabel: '', itemLabel: '',
  targetId: '', targetUrl: '', targetName: '', openInNewTab: false, requiresAppToken: false
};

/* Rows were keyed by array index, which is fine for a list you only append to
   and wrong the moment anything moves: React reuses the DOM node and the value
   you were typing follows the wrong row. Dragging makes that constant, so every
   item carries an id from the moment it exists to the moment it is saved.
   Stripped again in submit - the server neither wants it nor stores it. */
let nextRowId = 0;
const withRowId = (item) => ({ ...item, _rowId: `row-${nextRowId++}` });

/* The tabs. `All` is first and is the only one where dragging is possible: a
   filtered list hides rows, so a drop position in it does not mean what it
   appears to mean. */
const TYPE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'Report', label: 'Reports' },
  { key: 'PaginatedReport', label: 'Paginated' },
  { key: 'Dashboard', label: 'Dashboards' },
  { key: 'Website', label: 'Websites' }
];

export default function AdminProfiles() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [term, setTerm] = useState('');
  const { sort, toggle, sortRows } = useSort('name');

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['admin', 'profiles'], queryFn: admin.listProfiles
  });

  /**
   * Invalidate every admin query, not just the list.
   *
   * The editor reads ['admin', 'profile', id] while the list is
   * ['admin', 'profiles'] — different keys, so invalidating the list does not
   * prefix-match the detail query. With staleTime at 60s, reopening a profile
   * just after saving served the pre-save snapshot and looked like the save
   * had been discarded. The sidebar's ['navigation'] query had the same
   * problem, so the menu kept the old items too.
   */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin'] });
    queryClient.invalidateQueries({ queryKey: ['navigation'] });
  };

  const remove = useMutation({
    mutationFn: admin.deleteProfile,
    onSuccess: () => { setConfirming(null); invalidate(); }
  });

  // Copy, then open the new profile so it can be renamed and adjusted.
  const duplicate = useMutation({
    mutationFn: admin.duplicateProfile,
    onSuccess: ({ profileId }) => { invalidate(); setSelected(profileId); }
  });

  /* Entering a preview changes what every screen shows, so reload rather than
     patch the cache: a stale navigation or /api/me here would be a confusing
     half-preview. Landing on the home page is also what you want - the point
     is to see what this profile lands on. */
  const preview = useMutation({
    mutationFn: admin.startPreview,
    onSuccess: () => { window.location.href = '/'; }
  });

  const searched = useTextFilter(profiles, term, ['name']);
  const rows = sortRows(searched, {
    name: p => p.name,
    itemCount: p => p.itemCount,
    userCount: p => p.userCount
  });

  return (
    <div className="admin-page">
      <PageHeader title="Profile Management" count={profiles.length}>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setSelected('new')}>
          <i className="bi bi-plus-lg" /> New profile
        </button>
      </PageHeader>

      {profiles.length > 0 && (
        <div className="admin-toolbar">
          <SearchBox value={term} onChange={setTerm} placeholder="Search profiles…" />
        </div>
      )}

      {isLoading
        ? <div className="text-center py-5"><div className="spinner-border" /></div>
        : rows.length === 0
          ? <EmptyState
              icon={profiles.length ? 'bi-search' : 'bi-diagram-3'}
              title={profiles.length ? 'No profiles match that search' : 'No profiles yet'}
              action={!profiles.length && (
                <button type="button" className="btn btn-sm btn-primary"
                        onClick={() => setSelected('new')}>Create the first profile</button>
              )}
            >
              {profiles.length
                ? 'Try a different search term.'
                : <>A profile is a menu: a set of reports, dashboards and websites.
                   Assign it to users to give them access.</>}
            </EmptyState>
          : (
            <div className="admin-table-wrap">
              <table className="table table-sm admin-table mb-0">
                <thead>
                  <tr>
                    <SortHeader label="Profile" sortKey="name" sort={sort} onToggle={toggle} />
                    <th>Contents</th>
                    <SortHeader label="Items" sortKey="itemCount" sort={sort} onToggle={toggle}
                                style={{ width: 90 }} />
                    <SortHeader label="Users" sortKey="userCount" sort={sort} onToggle={toggle}
                                style={{ width: 90 }} />
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(p => (
                    <tr key={p.profileId}>
                      <td>
                        <button type="button" className="btn btn-link p-0 fw-medium text-start"
                                onClick={() => setSelected(p.profileId)}>
                          {p.name}
                        </button>
                        {p.homeItemName && (
                          <div className="admin-muted small">
                            Opens on {p.homeItemName}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap align-items-center">
                          {p.itemCount === 0
                            ? <Badge tone="warning">Empty menu</Badge>
                            : <span className="admin-muted small">
                                {p.itemCount} menu item{p.itemCount === 1 ? '' : 's'}
                              </span>}
                          {p.isAiEnabled && (
                            <Badge tone="info"
                                   title="Enabled for this profile. Each user must allow it too.">
                              AI
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td>{p.itemCount}</td>
                      <td>
                        {p.userCount === 0
                          ? <span className="admin-muted">none</span>
                          : p.userCount}
                      </td>
                      <td className="row-actions">
                        <button type="button" className="btn btn-sm btn-link"
                                title="Edit profile" aria-label={`Edit ${p.name}`}
                                onClick={() => setSelected(p.profileId)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button type="button" className="btn btn-sm btn-link"
                                title="Duplicate profile" aria-label={`Duplicate ${p.name}`}
                                disabled={duplicate.isPending}
                                onClick={() => duplicate.mutate(p.profileId)}>
                          <i className="bi bi-copy" />
                        </button>
                        <button type="button" className="btn btn-sm btn-link"
                                title="View the portal as this profile sees it"
                                aria-label={`View as ${p.name}`}
                                disabled={preview.isPending}
                                onClick={() => preview.mutate(p.profileId)}>
                          <i className="bi bi-eye" />
                        </button>
                        <button type="button" className="btn btn-sm btn-link text-danger"
                                title={p.userCount > 0
                                  ? 'Reassign its users before deleting'
                                  : 'Delete profile'}
                                aria-label={`Delete ${p.name}`}
                                disabled={p.userCount > 0}
                                onClick={() => setConfirming(p)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {duplicate.isError && (
        <div className="alert alert-danger py-2 small mt-3">
          Could not duplicate: {duplicate.error.body?.error ?? duplicate.error.message}
        </div>
      )}

      {selected && (
        <ProfileEditor
          profileId={selected === 'new' ? null : selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); invalidate(); }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title="Delete this profile?"
          confirmLabel="Delete profile"
          busy={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => remove.mutate(confirming.profileId)}
          body={
            <p className="mb-0">
              <strong>{confirming.name}</strong> and its {confirming.itemCount} menu
              item{confirming.itemCount === 1 ? '' : 's'} will be removed. No reports are
              deleted from Power BI — only this menu.
            </p>
          }
        />
      )}
    </div>
  );
}

/**
 * The Group box, committed when you leave it rather than on every keystroke.
 *
 * This has to be its own component with its own state, and the reason is
 * structural rather than stylistic.
 *
 * Sections are DERIVED from groupLabel: the rows are one flat list, and the
 * editor buckets them by label at render time into a <div> keyed on that
 * label. So a per-keystroke onChange moved the row into a different bucket on
 * the FIRST character - a bucket with a different key, which React builds as a
 * new subtree. The input being typed into was unmounted and a fresh one
 * mounted in its place: focus gone, caret gone, and the row jumped to the
 * bottom of the list because a brand-new group is appended last. Typing
 * "Finance" meant clicking back into the box seven times.
 *
 * Holding the draft locally means the shared state - and therefore the
 * grouping - does not move until the field is finished with. The row then
 * relocates once, deliberately, which is the behaviour you would ask for.
 *
 * Blur covers clicking Save: mousedown fires blur before click, so the value
 * is committed before submit reads it. Enter commits without waiting.
 */
function GroupLabelInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  /* Adopt an outside change, but never mid-edit. In practice `value` only
     changes as a result of committing, which regroups and remounts this
     anyway - this is here so that stops being load-bearing. */
  if (!focused && draft !== value) setDraft(value);

  return (
    <input className="form-control form-control-sm" value={draft}
           aria-label="Group heading" placeholder="Section"
           onFocus={() => setFocused(true)}
           onChange={e => setDraft(e.target.value)}
           onBlur={() => { setFocused(false); if (draft !== value) onCommit(draft); }}
           onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }} />
  );
}

/* ------------------------------ editor -------------------------------- */

function ProfileEditor({ profileId, onClose, onSaved }) {
  const isNew = !profileId;
  const { data: existing } = useQuery({
    queryKey: ['admin', 'profile', profileId],
    queryFn: () => admin.getProfile(profileId),
    enabled: !isNew
  });
  const { data: catalogue } = useQuery({
    queryKey: ['admin', 'catalogue'], queryFn: admin.getCatalogue
  });

  const [form, setForm] = useState({
    name: '', homeItemId: '', homeItemName: '', isAiEnabled: false, items: []
  });
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const sensors = useSensors(
    /* 6px before a drag starts, so a click on the handle is still a click and
       a slightly unsteady hand does not reorder the menu by accident. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (existing && !loaded) {
    setForm({
      name: existing.name,
      homeItemId: existing.homeItemId ?? '',
      homeItemName: existing.homeItemName ?? '',
      isAiEnabled: existing.isAiEnabled ?? false,
      items: existing.items.map(i => withRowId({
        itemType: i.itemType, surface: i.surface,
        groupLabel: i.groupLabel ?? '', itemLabel: i.itemLabel ?? '',
        targetId: i.targetId ?? '', targetUrl: i.targetUrl ?? '',
        targetName: i.targetName ?? '',
        openInNewTab: i.openInNewTab, requiresAppToken: i.requiresAppToken
      }))
    });
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: (payload) => isNew ? admin.createProfile(payload) : admin.updateProfile(profileId, payload),
    onSuccess: onSaved,
    onError: (err) => setError(
      err.body?.issues?.map(i => `${i.path}: ${i.message}`).join('; ')
      ?? err.body?.error ?? err.message)
  });

  const patchItem = (index, patch) => setForm(f => ({
    ...f, items: f.items.map((it, i) => (i === index ? { ...it, ...patch } : it))
  }));

  /**
   * Picking a report fills the Label with its name, unless the admin has
   * already typed something of their own. Storing the name means the menu
   * reads correctly even if the item is later pointed at a report that has
   * since been removed from the catalogue.
   */
  const selectTarget = (index, targetId) => setForm(f => ({
    ...f,
    items: f.items.map((it, i) => {
      if (i !== index) return it;
      const name = targetOptions(it.itemType)
        .find(o => sameId(o.id, targetId))?.name ?? it.targetName ?? '';
      const untouched = !it.itemLabel || it.itemLabel === it.targetName;
      return { ...it, targetId, targetName: name, itemLabel: untouched ? name : it.itemLabel };
    })
  }));

  /* Move a row up or down within its own group.
     The array positions do not change - the contents of the group's slots are
     swapped - which is what keeps other groups where they were when groups
     interleave. Same rule the drag handler follows. */
  const moveWithinGroup = (indices, index, delta) => setForm(f => {
    const at = indices.indexOf(index);
    const to = at + delta;
    if (at === -1 || to < 0 || to >= indices.length) return f;

    const items = [...f.items];
    const a = indices[at];
    const b = indices[to];
    [items[a], items[b]] = [items[b], items[a]];
    return { ...f, items };
  });

  /* Move a whole section, and everything in it.
     -----------------------------------------------------------------------
     Rows could only ever be reordered inside their own section, so the order
     of the sections themselves was whatever fell out of the array - fixable
     only by deleting a section and retyping it lower down.

     This rebuilds the list as contiguous blocks in the new section order, and
     that flattening is the part worth justifying, because it discards any
     interleaving that existed. It is safe because interleaving was never
     visible: groupNavItems keys its Map on [section, label], so a section is
     created at its FIRST occurrence and every later row with that label joins
     it wherever it sits in SortOrder. Two rows of "Finance" either side of a
     "Sales" row already render as one Finance section above Sales. So the
     menu after this is identical to the menu before, minus a distinction the
     sidebar never honoured.

     Within-section order survives: filter preserves relative order. */
  const moveGroup = (label, delta) => setForm(f => {
    const order = [];
    f.items.forEach(it => {
      const l = it.groupLabel || '';
      if (!order.includes(l)) order.push(l);
    });

    const at = order.indexOf(label);
    const to = at + delta;
    if (at === -1 || to < 0 || to >= order.length) return f;
    [order[at], order[to]] = [order[to], order[at]];

    return { ...f, items: order.flatMap(l => f.items.filter(it => (it.groupLabel || '') === l)) };
  });

  /* --------------------------- menu grouping ----------------------------
     The live menu is one flat list in SortOrder, grouped by GroupLabel at
     render time - so a group's items need not be adjacent in the array, and a
     group appears wherever its first item falls. The editor mirrors that
     exactly rather than inventing its own model: same order, same grouping,
     so what an administrator arranges here is what a user sees.

     Reordering therefore permutes a group's OCCUPIED SLOTS rather than moving
     rows through the array. Everything outside the group keeps its position,
     which is what "reorder within a group only" has to mean when groups can
     interleave. -------------------------------------------------------- */
  const groups = [];
  form.items.forEach((item, index) => {
    const label = item.groupLabel || '';
    let group = groups.find(g => g.label === label);
    if (!group) { group = { label, indices: [] }; groups.push(group); }
    group.indices.push(index);
  });


  const filtering = typeFilter !== 'all';
  const visible = (item) => !filtering || item.itemType === typeFilter;

  const counts = TYPE_TABS.reduce((acc, t) => ({
    ...acc,
    [t.key]: t.key === 'all'
      ? form.items.length
      : form.items.filter(i => i.itemType === t.key).length
  }), {});


  const onDragEnd = (event, indices) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setForm(f => {
      const rows = indices.map(i => f.items[i]);
      const from = rows.findIndex(r => r._rowId === active.id);
      const to = rows.findIndex(r => r._rowId === over.id);
      if (from === -1 || to === -1) return f;

      const reordered = [...rows];
      reordered.splice(to, 0, reordered.splice(from, 1)[0]);

      // Write the group's rows back into the same slots, in the new order.
      const items = [...f.items];
      indices.forEach((slot, n) => { items[slot] = reordered[n]; });
      return { ...f, items };
    });
  };

  /* A new row, dropped into a named section.
     -----------------------------------------------------------------------
     There was one button, at the bottom, which appended a row carrying
     `f.items.at(-1)?.groupLabel` - the LAST row's section, whichever that
     happened to be. Adding to any other section meant adding at the end and
     then retyping the heading, and retyping the heading was itself broken.
     Together those made sections look read-only.

     `label` is matched against the same `groupLabel || ''` the grouping uses,
     and the row is inserted after the section's last member so it lands where
     it appears to. Read from `f` inside the updater rather than closing over
     the render's `groups`, so a queued update cannot insert at a stale index.

     While a type filter is on the new row adopts that type - adding a website
     from the Websites tab and watching it vanish because it defaulted to
     Report would be its own small bug. */
  const addItemToGroup = (label) => setForm(f => {
    let at = f.items.length;
    for (let n = f.items.length - 1; n >= 0; n--) {
      if ((f.items[n].groupLabel || '') === label) { at = n + 1; break; }
    }
    const items = [...f.items];
    items.splice(at, 0, withRowId({
      ...BLANK_ITEM,
      groupLabel: label,
      itemType: filtering ? typeFilter : BLANK_ITEM.itemType
    }));
    return { ...f, items };
  });

  // The bottom button now means one thing only: start a new top-level item.
  // Per-section adding lives on the sections, where it can be unambiguous.
  const addItem = () => addItemToGroup('');

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    save.mutate({
      name: form.name,
      homeItemId: form.homeItemId || null,
      homeItemName: form.homeItemName || null,
      isAiEnabled: form.isAiEnabled,
      items: form.items.map(({ targetName: _unused, _rowId: _key, ...i }) => ({
        ...i,
        groupLabel: i.groupLabel || null,
        itemLabel: i.itemLabel || null,
        targetId: i.itemType === 'Website' ? null : (i.targetId || null),
        targetUrl: i.itemType === 'Website' ? (i.targetUrl || null) : null
      }))
    });
  };

  const sameId = (a, b) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();

  const targetOptions = (itemType) => {
    if (itemType === 'Dashboard') return catalogue?.dashboards ?? [];
    if (itemType === 'PaginatedReport') {
      return (catalogue?.reports ?? []).filter(r => r.reportType === 'PaginatedReport');
    }
    return (catalogue?.reports ?? []).filter(r => r.reportType !== 'PaginatedReport');
  };

  /**
   * Options for one row, guaranteeing the row's own value is among them.
   *
   * A controlled <select> whose value matches no <option> renders blank and
   * then writes null back on the next save — silently destroying the target.
   * That is what made this look like "the target isn't saving". If the stored
   * id is missing from the catalogue (never synced, deleted from the
   * workspace, or classified as a different report type) we add it back as an
   * explicit entry so the value survives and the reason is visible.
   */
  const optionsFor = (item) => {
    const options = targetOptions(item.itemType);
    if (!item.targetId) return { options, orphan: false };
    if (options.some(o => sameId(o.id, item.targetId))) return { options, orphan: false };
    return {
      options: [
        { id: item.targetId, name: item.targetName || item.targetId, missing: true },
        ...options
      ],
      orphan: true
    };
  };

  const reportItems = form.items.filter(i => i.itemType !== 'Website' && i.targetId);
  const catalogueEmpty = catalogue
    && catalogue.reports.length === 0 && catalogue.dashboards.length === 0;

  return (
    <Modal
      title={isNew ? 'New profile' : `Edit “${form.name}”`}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <span className="admin-muted small me-auto">
            {form.items.length} menu item{form.items.length === 1 ? '' : 's'}
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="profile-form" className="btn btn-sm btn-primary"
                  disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save profile'}
          </button>
        </>
      }
    >
      <form id="profile-form" className="modal-body" onSubmit={submit}>
        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        {catalogueEmpty && (
          <div className="alert alert-warning py-2 small">
            <strong>No Power BI content has been synced yet.</strong> The Target list will
            be empty for reports and dashboards until you sync the workspace under{' '}
            <Link to="/admin/catalogue">Power BI Content</Link>.
          </div>
        )}

        <FormSection title="Profile">
          <div className="row g-3">
            <div className="col-md-5">
              <label className="form-label" htmlFor="profileName">Name</label>
              <input id="profileName" className="form-control form-control-sm" required
                     autoFocus value={form.name}
                     onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="col-md-7">
              <label className="form-label" htmlFor="homeItem">Opens on</label>
              <select id="homeItem" className="form-select form-select-sm"
                      value={form.homeItemId}
                      onChange={e => {
                        const opt = reportItems.find(i => sameId(i.targetId, e.target.value));
                        const name = opt?.itemLabel || opt?.targetName || '';
                        setForm({ ...form, homeItemId: e.target.value, homeItemName: name });
                      }}>
                <option value="">— customer logo —</option>
                {reportItems.map(i => (
                  <option key={i.targetId} value={i.targetId}>
                    {i.itemLabel || i.targetName || i.targetId}
                  </option>
                ))}
                {/* Keep a home item that is no longer in the menu selectable,
                    rather than silently clearing it on the next save. */}
                {form.homeItemId
                  && !reportItems.some(i => sameId(i.targetId, form.homeItemId)) && (
                  <option value={form.homeItemId}>
                    {form.homeItemName || form.homeItemId} — no longer in this menu
                  </option>
                )}
              </select>
              <div className="form-text">
                The landing page for users on this profile. Only items already in the
                menu below can be chosen.
              </div>
            </div>
          </div>

          <div className="mt-3">
            <div className="form-check">
              <input id="profileAi" className="form-check-input" type="checkbox"
                     checked={form.isAiEnabled}
                     onChange={e => setForm({ ...form, isAiEnabled: e.target.checked })} />
              <label className="form-check-label" htmlFor="profileAi">
                Vizora AI Data Assistant
              </label>
            </div>
            {/* The other half of the rule, stated where the decision is made.
                Two switches that each look sufficient on their own is the
                surest way to generate a support call. */}
            <div className="form-text">
              {form.isAiEnabled
                ? 'Each user on this profile also needs the assistant enabled on '
                  + 'their own record, under User Setup.'
                : 'With this clear, nobody on this profile can open the assistant, '
                  + 'whatever their own setting says.'}
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Menu"
          hint="Items appear in this order. Give several items the same Group to nest them under one collapsible heading; leave Label blank for a top-level link.">
          {form.items.length === 0 ? (
            <EmptyState icon="bi-list-ul" title="This menu is empty"
                        action={
                          <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>
                            <i className="bi bi-plus-lg" /> Add the first item
                          </button>
                        }>
              Users assigned to this profile would see an empty sidebar.
            </EmptyState>
          ) : (
            <>
              {/* Tabs filter one list; they do not create four. The menu is a
                  single ordered sequence and a group can hold a report, a
                  website and a dashboard together, so a per-type list would be
                  showing an order that does not exist. */}
              <div className="item-tabs" role="tablist" aria-label="Filter menu items by type">
                {TYPE_TABS.map(t => (
                  <button
                    key={t.key} type="button" role="tab"
                    aria-selected={typeFilter === t.key}
                    className={`item-tab${typeFilter === t.key ? ' is-active' : ''}`}
                    onClick={() => setTypeFilter(t.key)}>
                    {t.label}
                    <span className="item-tab-count">{counts[t.key]}</span>
                  </button>
                ))}
              </div>

              {filtering && (
                <p className="admin-muted small mb-2">
                  <i className="bi bi-info-circle me-1" aria-hidden="true" />
                  Showing {TYPE_TABS.find(t => t.key === typeFilter)?.label.toLowerCase()} only.
                  Reordering is off while a filter is on — a drop position in a
                  list with rows hidden would not mean what it looks like.
                  Switch to <strong>All</strong> to rearrange the menu.
                </p>
              )}

              <div className="item-row-grid admin-muted small mb-1 d-none d-lg-grid">
                <span /><span>Type</span><span>Surface</span><span>Group</span>
                <span>Label</span><span>Target</span><span />
              </div>

              {groups.map((group, gi) => {
                const shown = group.indices.filter(i => visible(form.items[i]));
                if (!shown.length) return null;

                return (
                  <div className="item-group" key={group.label || '(none)'}>
                    {/* The heading users will actually see, or a note that this
                        block has none - which is a real state, not a mistake:
                        an item with no group is a top-level link. */}
                    <div className="item-group-head">
                      {group.label
                        ? <span className="item-group-name">{group.label}</span>
                        : <span className="item-group-name is-none">Top level — no group heading</span>}
                      <span className="admin-muted small">
                        {shown.length} item{shown.length === 1 ? '' : 's'}
                      </span>
                      {/* Hidden while filtering, for the same reason row
                          reordering is: with sections missing from the list,
                          "move up" would land somewhere you cannot see. */}
                      {!filtering && groups.length > 1 && (
                        <div className="item-group-move">
                          <button type="button" className="btn btn-link btn-sm"
                                  disabled={gi === 0}
                                  onClick={() => moveGroup(group.label, -1)}
                                  aria-label={`Move ${group.label || 'the top-level items'} up`}>
                            <i className="bi bi-chevron-up" />
                          </button>
                          <button type="button" className="btn btn-link btn-sm"
                                  disabled={gi === groups.length - 1}
                                  onClick={() => moveGroup(group.label, 1)}
                                  aria-label={`Move ${group.label || 'the top-level items'} down`}>
                            <i className="bi bi-chevron-down" />
                          </button>
                        </div>
                      )}
                      <button type="button" className="btn btn-sm btn-link item-group-add"
                              onClick={() => addItemToGroup(group.label)}
                              title={group.label
                                ? `Add an item to ${group.label}`
                                : 'Add a top-level item'}>
                        <i className="bi bi-plus-lg" aria-hidden="true" /> Add
                      </button>
                    </div>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(e) => onDragEnd(e, group.indices)}>
                      <SortableContext
                        items={group.indices.map(i => form.items[i]._rowId)}
                        strategy={verticalListSortingStrategy}>

              {shown.map((i) => {
                const item = form.items[i];
                return (
                <SortableItemRow key={item._rowId} id={item._rowId} disabled={filtering}>
                  <div className="item-row-grid">
                    <select className="form-select form-select-sm" value={item.itemType}
                            aria-label="Item type"
                            onChange={e => patchItem(i, {
                              itemType: e.target.value,
                              targetId: '', targetUrl: '', targetName: ''
                            })}>
                      <option value="Report">Report</option>
                      <option value="PaginatedReport">Paginated report</option>
                      <option value="Dashboard">Dashboard</option>
                      <option value="Website">Website</option>
                    </select>

                    <select className="form-select form-select-sm" value={item.surface}
                            aria-label="Surface"
                            onChange={e => patchItem(i, { surface: e.target.value })}>
                      <option value="Desktop">Desktop</option>
                      <option value="Mobile">Mobile</option>
                      <option value="Both">Both</option>
                    </select>

                    <GroupLabelInput value={item.groupLabel}
                                     onCommit={v => patchItem(i, { groupLabel: v })} />

                    <input className="form-control form-control-sm" value={item.itemLabel}
                           aria-label="Item label" placeholder="(top level)"
                           onChange={e => patchItem(i, { itemLabel: e.target.value })} />

                    {item.itemType === 'Website' ? (
                      <input className="form-control form-control-sm" type="url"
                             aria-label="Website URL" placeholder="https://…"
                             value={item.targetUrl}
                             onChange={e => patchItem(i, { targetUrl: e.target.value })} />
                    ) : (
                      <select className={`form-select form-select-sm${
                                optionsFor(item).orphan ? ' border-warning' : ''}`}
                              value={item.targetId}
                              aria-label="Target"
                              onChange={e => selectTarget(i, e.target.value)}>
                        <option value="">— select —</option>
                        {optionsFor(item).options.map(o => (
                          <option key={o.id} value={o.id}>
                            {o.name}{o.missing ? ' — not in catalogue' : ''}
                          </option>
                        ))}
                      </select>
                    )}

                    <div className="d-flex align-items-center gap-1">
                      {/* Kept alongside dragging, and now scoped to the group
                          like the drag is. They need no discovery, they work on
                          a touch screen, and they are the fallback if a drag
                          library ever misbehaves. Hidden while filtering for
                          the same reason dragging is disabled. */}
                      {!filtering && (
                        <div className="item-row-handle">
                          <button type="button" className="btn btn-link btn-sm"
                                  disabled={group.indices.indexOf(i) === 0}
                                  onClick={() => moveWithinGroup(group.indices, i, -1)}
                                  aria-label="Move up">
                            <i className="bi bi-chevron-up" />
                          </button>
                          <button type="button" className="btn btn-link btn-sm"
                                  disabled={group.indices.indexOf(i) === group.indices.length - 1}
                                  onClick={() => moveWithinGroup(group.indices, i, 1)}
                                  aria-label="Move down">
                            <i className="bi bi-chevron-down" />
                          </button>
                        </div>
                      )}
                      <button type="button" className="btn btn-sm btn-link text-danger"
                              aria-label="Remove item"
                              onClick={() => setForm(f => ({
                                ...f, items: f.items.filter((_, x) => x !== i)
                              }))}>
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </div>

                  {item.itemType !== 'Website' && optionsFor(item).orphan && (
                    <div className="small text-warning-emphasis ps-1">
                      <i className="bi bi-exclamation-triangle-fill" />{' '}
                      This report is not in the synced catalogue. It will still be saved,
                      but check it exists in the workspace and run a sync under{' '}
                      <Link to="/admin/catalogue">Power BI Content</Link>.
                    </div>
                  )}

                  {item.itemType === 'Website' && (
                    <div className="d-flex gap-3 ps-1">
                      <div className="form-check form-check-inline m-0">
                        <input className="form-check-input" type="checkbox" id={`nt${i}`}
                               checked={item.openInNewTab}
                               onChange={e => patchItem(i, { openInNewTab: e.target.checked })} />
                        <label className="form-check-label small" htmlFor={`nt${i}`}>
                          Open in a new tab
                        </label>
                      </div>
                      <div className="form-check form-check-inline m-0">
                        <input className="form-check-input" type="checkbox" id={`at${i}`}
                               checked={item.requiresAppToken}
                               onChange={e => patchItem(i, { requiresAppToken: e.target.checked })} />
                        <label className="form-check-label small" htmlFor={`at${i}`}>
                          Send a signed app token
                        </label>
                      </div>
                    </div>
                  )}
                </SortableItemRow>
                );
              })}

                      </SortableContext>
                    </DndContext>
                  </div>
                );
              })}

              <button type="button" className="btn btn-sm btn-outline-primary mt-3"
                      onClick={addItem}>
                <i className="bi bi-plus-lg" /> Add a top-level item
              </button>
              <div className="form-text">
                Use <strong>Add</strong> on a section heading to put an item inside
                that section.
              </div>
            </>
          )}
        </FormSection>
      </form>
    </Modal>
  );
}
